import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import Link from '@vs-shop/image-link/Link';

import type { VsLinkProps } from '@vs-shop/headless/vsLink';

import { clsxm } from '@vs-shop/utils';

/**
 * The strip of tabs over a listing of articles: every part of the magazine,
 * each with how much is filed under it, and one of them the listing on screen.
 * Flat named parts a tenant arranges in its own JSX, each painting itself.
 *
 *   <ArticleSectionTabs aria-label={label}>
 *     <ArticleSectionTabsList>
 *       {tabs.map((tab) => (
 *         <ArticleSectionTab key={tab.slug}>
 *           <ArticleSectionTabLink href={tab.href} aria-current={tab.selected ? 'page' : undefined}>
 *             <ArticleSectionTabLabel state={stateOf(tab)}>{tab.name}</ArticleSectionTabLabel>
 *             <ArticleSectionTabCount state={stateOf(tab)}>{tab.count}</ArticleSectionTabCount>
 *           </ArticleSectionTabLink>
 *           {tab.selected ? <ArticleSectionTabMarker /> : null}
 *         </ArticleSectionTab>
 *       ))}
 *     </ArticleSectionTabsList>
 *   </ArticleSectionTabs>
 *
 * **WHICH tabs the strip has is data, and it arrives as composed children** —
 * the sections a catalogue reports, the counts it reports for them, and the
 * "everything" tab a tenant puts first are all the tenant's. The strip
 * contributes the row, the scroll, the rule under it and the two appearances a
 * tab can be in.
 *
 * **The marker under the selected tab is a part, not a class.** It is an
 * element in the tab's own positioning context, so a tab that is not the
 * listing on screen composes no marker rather than an invisible one.
 *
 * Server-first: every part is a plain server component. Whether following a tab
 * is a navigation or a filter the tenant intercepts is its own affair.
 */

/**
 * The strip: one row that scrolls sideways rather than wrapping, over the rule
 * that separates it from the listing.
 *
 * Scrolling rather than wrapping because a magazine with a dozen parts would
 * otherwise push the first article a row further down at every width, and a tab
 * strip that changes height is a page that moves while it loads.
 */
const articleSectionTabsClasses =
  'relative mx-auto w-full max-w-full overflow-x-auto border-b border-b-foreground/10';

export type ArticleSectionTabsProps = ComponentProps<'nav'>;

/**
 * The tab strip.
 *
 * A `<nav>` because it IS a set of routes into the catalogue, and the label
 * naming it is the tenant's — only a tenant knows whether its parts are
 * sections, rubrics or channels.
 */
export const ArticleSectionTabs = ({ className, ...props }: ArticleSectionTabsProps) => (
  <nav
    data-slot="article-section-tabs"
    className={clsxm(articleSectionTabsClasses, className)}
    {...props}
  />
);

/**
 * The row of tabs.
 *
 * `inline-flex` so the row is as wide as its tabs rather than as wide as the
 * strip — which is what gives the strip above something to scroll.
 */
const articleSectionTabsListClasses =
  'inline-flex items-center gap-8 pb-4 whitespace-nowrap xl:gap-16';

export type ArticleSectionTabsListProps = ComponentProps<'ul'>;

/** The tabs. */
export const ArticleSectionTabsList = ({ className, ...props }: ArticleSectionTabsListProps) => (
  <ul
    data-slot="article-section-tabs-list"
    className={clsxm(articleSectionTabsListClasses, className)}
    {...props}
  />
);

/**
 * One tab's cell.
 *
 * Positioned, because the marker under a selected tab hangs below the strip's
 * padding and measures itself against this cell rather than against the row.
 */
const articleSectionTabClasses = 'relative';

export type ArticleSectionTabProps = ComponentProps<'li'>;

/** A tab's cell. */
export const ArticleSectionTab = ({ className, ...props }: ArticleSectionTabProps) => (
  <li
    data-slot="article-section-tab"
    className={clsxm(articleSectionTabClasses, className)}
    {...props}
  />
);

/**
 * The tab itself: its name beside how much is filed under it.
 *
 * It paints no state of its own — the name and the count each say it — so it
 * carries no axis. Whether the tab is the listing on screen is stated where a
 * shopper's software reads it, as the `aria-current` the composition passes.
 */
const articleSectionTabLinkClasses = 'flex items-center gap-4 text-sm/5';

export type ArticleSectionTabLinkProps = VsLinkProps;

/** A tab. */
export const ArticleSectionTabLink = ({ className, ...props }: ArticleSectionTabLinkProps) => (
  <Link
    data-slot="article-section-tab-link"
    className={clsxm(articleSectionTabLinkClasses, className)}
    {...props}
  />
);

/**
 * Whether a tab is the listing on screen.
 *
 * Required and without `defaultVariants`, so no tab's appearance is decided by
 * an omission.
 */
export const articleSectionTabLabelVariants = cva(['font-heading', 'tracking-wide uppercase'], {
  variants: {
    state: {
      /** The listing on screen is this tab's. */
      selected: 'text-foreground',
      /** A part of the magazine the shopper is not reading. */
      available: 'text-foreground-muted',
    },
  },
});

type ArticleSectionTabStateName = NonNullable<
  VariantProps<typeof articleSectionTabLabelVariants>['state']
>;

export type ArticleSectionTabState = ArticleSectionTabStateName;

/** Derived from the recipe and bound to it by `satisfies`, so the two cannot drift. */
export const ArticleSectionTabState = {
  /** The listing on screen is this tab's. */
  SELECTED: 'selected',
  /** A part of the magazine the shopper is not reading. */
  AVAILABLE: 'available',
} as const satisfies Record<string, ArticleSectionTabStateName>;

/** Required and closed — not `VariantProps`, which makes the axis optional. */
export type ArticleSectionTabStateProps = { readonly state: ArticleSectionTabState };

export type ArticleSectionTabLabelProps = ComponentProps<'span'> & ArticleSectionTabStateProps;

/** A tab's name. */
export const ArticleSectionTabLabel = ({
  className,
  state,
  ...props
}: ArticleSectionTabLabelProps) => (
  <span
    data-slot="article-section-tab-label"
    className={clsxm(articleSectionTabLabelVariants({ state }), className)}
    {...props}
  />
);

/**
 * How much is filed under a tab, in a pill that fills in once the tab is the one
 * on screen.
 *
 * The recipe carries the values the section shipped with, and a tenant whose
 * palette needs something else says so at its own call site: a brand whose
 * `primary` is dark reads its filled pill in `primary-foreground` and passes
 * that through `className`. The shared recipe does not chase it — changing a
 * value here changes every tenant already rendering it.
 */
export const articleSectionTabCountVariants = cva(
  'text-foreground flex items-center justify-center rounded-lg px-3',
  {
    variants: {
      state: {
        selected: 'bg-primary',
        available: 'bg-foreground/10',
      },
    },
  }
);

export type ArticleSectionTabCountProps = ComponentProps<'span'> & ArticleSectionTabStateProps;

/** A tab's count. */
export const ArticleSectionTabCount = ({
  className,
  state,
  ...props
}: ArticleSectionTabCountProps) => (
  <span
    data-slot="article-section-tab-count"
    className={clsxm(articleSectionTabCountVariants({ state }), className)}
    {...props}
  />
);

/**
 * The bar under the tab whose listing is on screen.
 *
 * It hangs below the strip's own padding and meets the rule the strip draws, so
 * the selected tab reads as attached to the listing under it. Square-topped
 * once there is room, because a rounded bar at laptop width reads as a pill
 * that lost its other half.
 */
const articleSectionTabMarkerClasses =
  'absolute -bottom-4 left-0 h-1 w-full rounded-t-full bg-primary lap:rounded-t-none';

export type ArticleSectionTabMarkerProps = ComponentProps<'span'>;

/** The selected tab's bar. */
export const ArticleSectionTabMarker = ({ className, ...props }: ArticleSectionTabMarkerProps) => (
  <span
    data-slot="article-section-tab-marker"
    className={clsxm(articleSectionTabMarkerClasses, className)}
    {...props}
  />
);
